// Guessed English translations for Thai tokens that miss the dictionary,
// e.g. the loanword เรียลไทม์ (no entry, but its spelling recovers to
// "real time"). The lexicon is generated from thai2english.db's eng_words
// by scripts/build-loanword-index.ts.
import {
   parseSyllable,
   romanizeSyllableForGuess,
   guessRomanizationAlternates,
   thaiGuessKeys,
} from 'nlp'

// A guess is a single user-visible line, so all of these err towards
// returning null (today's behavior) over a nonsense match.
const MAX_SYLLABLES = 8
const MAX_GROUPS = 3
// Must clear scripts/build-loanword-index.ts MIN_COMMONNESS with room:
// rarer words make bad guesses even when the spelling converges.
const MIN_GUESS_SCORE = 60
// Multi-word guesses compound the risk, so each word of one needs a more
// distinctive key than a whole-token match does (blocks e.g. a stray ข้อ
// romanization "ko" matching "go").
const MIN_KEY_LENGTH_IN_SPLIT = 3
// Pattern alternates multiply span spellings; defaults always come first
const MAX_SPAN_SPELLINGS = 8

interface SpanMatch {
   word: string
   score: number
   keyLength: number
}

/**
 * Guesses the English source of an out-of-dictionary Thai word from its
 * syllables (segmentSyllables output), matching contiguous syllable groups
 * against the bundled key index: whole token first (คอมพิวเตอร์ →
 * "computer"), then splits joined with spaces (เรียลไทม์ → "real time").
 * Returns null when no confident cover exists — the caller keeps its
 * existing behavior, so this can only ever fill a blank entry.
 */
export function guessLoanwordEnglish(
   syllables: string[],
   index: Record<string, string>
): string | null {
   if (syllables.length < 1 || syllables.length > MAX_SYLLABLES) return null

   // Each syllable reads as its default romanization plus any hardcoded
   // pattern alternates (เ-ียร์ as -eer etc.), default first
   const spellingOptions: string[][] = []
   for (const syllableText of syllables) {
      const syllable = parseSyllable(syllableText)
      if (!syllable) return null
      const romanized = romanizeSyllableForGuess(syllable)
      spellingOptions.push([romanized, ...guessRomanizationAlternates(romanized)])
   }

   // All-defaults spelling stays first through the cartesian expansion, so
   // the first hit is always the most faithful reading (แอป finds "app"
   // before its อั-variant finds "up")
   const spanSpellings = (from: number, to: number): string[] => {
      let spellings = ['']
      for (let i = from; i < to; i++) {
         spellings = spellings.flatMap((prefix) =>
            spellingOptions[i].map((option) => prefix + option)
         )
         if (spellings.length > MAX_SPAN_SPELLINGS) {
            spellings = spellings.slice(0, MAX_SPAN_SPELLINGS)
         }
      }
      return spellings
   }

   const matchSpan = (from: number, to: number): SpanMatch | null => {
      for (const spelling of spanSpellings(from, to)) {
         for (const key of thaiGuessKeys(spelling)) {
            const entry = index[key]
            if (!entry) continue
            const [word, score] = entry.split('|')
            if (Number(score) < MIN_GUESS_SCORE) continue
            return { word, score: Number(score), keyLength: key.length }
         }
      }
      return null
   }

   // bestFrom[i] covers syllables[i..n): fewest groups, then highest summed
   // score. Span lengths are tried longest-first so ties resolve to the
   // longer first group — same shape as segmentUnknownWord.
   const n = syllables.length
   const bestFrom: { groups: number; score: number }[] = new Array(n + 1).fill(null)
   const spanChoice: (SpanMatch & { length: number })[] = new Array(n)
   bestFrom[n] = { groups: 0, score: 0 }

   for (let i = n - 1; i >= 0; i--) {
      for (let length = n - i; length >= 1; length--) {
         const rest = bestFrom[i + length]
         if (!rest || rest.groups + 1 > MAX_GROUPS) continue

         const match = matchSpan(i, i + length)
         if (!match) continue
         // Only a whole-token match may use a short key
         if (length < n && match.keyLength < MIN_KEY_LENGTH_IN_SPLIT) continue

         const candidate = { groups: rest.groups + 1, score: rest.score + match.score }
         const current = bestFrom[i]
         if (
            !current ||
            candidate.groups < current.groups ||
            (candidate.groups === current.groups && candidate.score > current.score)
         ) {
            bestFrom[i] = candidate
            spanChoice[i] = { ...match, length }
         }
      }
   }

   if (!bestFrom[0]) return null

   const words: string[] = []
   for (let i = 0; i < n; i += spanChoice[i].length) {
      words.push(spanChoice[i].word)
   }
   return words.join(' ')
}
